import React, { useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { Formik } from "formik";
import * as Yup from "yup";
import LoadingSpinner from "../../Components/spinner";
import { toast } from "sonner";
import {
  addNewCategory,
  editCategory,
  uploadCatImage,
} from "../../services/axios/apiManager";
import { handleErrors } from "../../services/dataManager";
import { useNavigate } from "react-router-dom";
import { baseURL } from "../../services/axios/eps";

const AddEditCatModal = ({
  selectedCategory,
  setSelectedCategory,
  showModal,
  setShowModal,
  fetchCategories,
  pagination,
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [imageURL, setImageURL] = useState(null);
  const [imageChanged, setImageChanged] = useState(false);

  // // // Close Add Edit Category Modal
  const handleCloseAddEditModal = () => {
    setSelectedCategory({});
    setImageURL(null);
    setShowModal(false);
  };

  // // // Validations
  const catValidations = Yup.object().shape({
    name: Yup.string()
      .trim()
      .min(2, "Category name must be at least 2 characters")
      .max(100, "Category name cannot exceed 100 characters")
      .required("Category name is required"),
    image: Yup.mixed().required("Icon is required"),
  });

  // // Add Edit Category Logic
  const handleAddEditTag = async (data) => {
    const { image, ...values } = data;
    if (selectedCategory?.id) {
      const editResponse = await editCategory({
        id: selectedCategory.id,
        ...values,
      });
      if (editResponse?.status === 200) {
        if (editResponse?.data?.success) {
          await uploadImage(selectedCategory.id, image);
          await fetchCategories(pagination);

          toast.success("Category has been updated!", { duration: 2000 });
        }
        handleCloseAddEditModal();
      } else {
        toast.error("Category has not been updated, try again!", {
          duration: 2000,
        });
      }
    } else {
      const addResponse = await addNewCategory(values);
      if (addResponse?.status === 200) {
        const id = addResponse?.data?.data?.id;
        if (id) {
          await uploadImage(id, image);
        }
        await fetchCategories(pagination);

        toast.success("Category has been added", {
          duration: 2000,
        });
        handleCloseAddEditModal();
      } else {
        toast.error("Category has not been added, try again!", {
          duration: 2000,
        });
      }
    }
  };
  console.log("imageURL :", imageURL);

  // // // Make Image URL Logic
  const makeImageURL = (file) => {
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      setImageURL(previewUrl);
      setImageChanged(true);
    }
  };

  // // // Upload Image Logic
  const uploadImage = async (id, icon) => {
    if (imageChanged) {
      const formData = new FormData();
      formData.append("image", icon);
      const res = await uploadCatImage(id, formData);
      if (res?.data.success) {
        toast.success("Category image has been uploaded", { duration: 2000 });
      } else {
        handleErrors(res, navigate);
      }
    }
  };

  return (
    <Modal
      show={showModal}
      onHide={handleCloseAddEditModal}
      centered
      className="text-light"
      backdrop="static"
    >
      <Modal.Header closeButton className="bg-dark text-light border-0">
        <Modal.Title>
          {selectedCategory?.id ? "Edit Category" : "Add Category"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="bg-dark text-light">
        <Formik
          initialValues={{
            name: selectedCategory?.name || "",
            status: selectedCategory?.status,
            image: selectedCategory?.image || null,
          }}
          enableReinitialize={true}
          validationSchema={catValidations}
          onSubmit={async (values) => {
            try {
              setLoading(true);
              await handleAddEditTag(values);
            } catch (error) {
              console.log("Error upon Add Update Tag", error);
            } finally {
              setLoading(false);
            }
          }}
        >
          {({
            handleChange,
            setFieldValue,
            handleSubmit,
            values,
            errors,
            touched,
          }) => (
            <div>
              <Form.Group className="mb-3">
                <Form.Label>Tag Name</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter tag name"
                  data-testid="tag-name"
                  required
                  value={values.name}
                  onChange={handleChange("name")}
                  className="bg-dark text-light border-light input-placeholder-light"
                />
                {errors?.name && touched?.name ? (
                  <span className="text-danger">{errors?.name}</span>
                ) : null}
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Status</Form.Label>
                <Form.Select
                  value={values.status}
                  data-testid="tag-status"
                  onChange={handleChange("status")}
                  className="bg-dark border-light text-light"
                >
                  <option value="1">Active</option>
                  <option value="0">Inactive</option>
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Image</Form.Label>
                <Form.Control
                  type="file"
                  data-testid="tag-image"
                  accept="image/*"
                  className="bg-dark text-light border-light"
                  onChange={(event) => {
                    if (event?.currentTarget?.files?.length) {
                      setFieldValue("image", event.currentTarget.files[0]);
                      makeImageURL(event.currentTarget.files[0]);
                    } else {
                      setFieldValue("image", selectedCategory.image);
                      setImageURL(null);
                      setImageChanged(false);
                    }
                  }}
                />

                {(imageURL || selectedCategory?.image) && (
                  <img
                    crossorigin="anonymous"
                    src={
                      imageURL
                        ? imageURL
                        : selectedCategory?.image
                          ? `${baseURL}/uploads/file/${selectedCategory.image}`
                          : null
                    }
                    style={{
                      maxWidth: "50%",
                      marginTop: "10px",
                      borderRadius: "5px",
                    }}
                    data-testid="tag-image-render"
                  />
                )}
                {errors?.image && touched?.image ? (
                  <span className="text-danger">{errors?.image}</span>
                ) : null}
              </Form.Group>
              <div className="constainer-fluid">
                <div className="d-flex justify-content-end" data-testid="spinner-lg">
                  {loading && (
                    <div className="mt-4 mx-2" >
                      <LoadingSpinner size={"lg"} />
                    </div>
                  )}
                  <Button
                    variant="secondary"
                    onClick={() => {
                      handleCloseAddEditModal();
                    }}
                    className="mt-3 mx-2"
                  >
                    Close
                  </Button>
                  <Button
                    variant="success"
                    className="mt-3"
                    onClick={() => {
                      handleSubmit();
                    }}
                  >
                    {selectedCategory?.id ? "Update" : "Add New Category"}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </Formik>
      </Modal.Body>
    </Modal>
  );
};
export default AddEditCatModal;
